/**
 * Apply a terminal Otto action: validate the draft, stash it under a one-shot
 * nonce, and navigate to the matching creation form pre-filled and ready for the
 * human to review + Save/Send. Otto never commits — this only OPENS the form.
 *
 * The validators + the sessionStorage handoff live inside the walled CRM
 * sub-app (they own the draft shapes); the parent reaches them via `@crm-src/*`.
 */
import type { NavigateFunction } from 'react-router-dom';

import type { OttoActionEvent } from '@xop/dashboard-shared';
import { validateOttoInvoiceDraft } from '@crm-src/components/generate-invoice-form/data-loaders';
import { validateOttoCampaignDraft } from '@crm-src/lib/otto-campaign-draft';
import { stashOttoDraft } from '@crm-src/lib/otto-draft-handoff';

export interface AppliedAction {
  /** A short status line for the cloak HUD while the form opens. */
  label: string;
  /** The route the human was taken to (also handy for tests). */
  path: string;
}

/**
 * Validate + apply a terminal action. Throws (so the caller can surface an
 * error in the cloak) if the draft is malformed. On success the browser is
 * already navigating to the pre-filled form.
 */
export function applyOttoAction(action: OttoActionEvent, navigate: NavigateFunction): AppliedAction {
  switch (action.kind) {
    case 'prefill_invoice': {
      const draft = validateOttoInvoiceDraft(action.draft);
      const nonce = stashOttoDraft({ ...draft, document_type: 'invoice' });
      const path = `/crm/invoices/generate?draft=${encodeURIComponent(nonce)}`;
      navigate(path);
      return { label: 'Opening the invoice — pre-filled and ready to review.', path };
    }
    case 'prefill_quote': {
      const draft = validateOttoInvoiceDraft(action.draft);
      const nonce = stashOttoDraft({ ...draft, document_type: 'quote' });
      const path = `/crm/quotes/generate?draft=${encodeURIComponent(nonce)}`;
      navigate(path);
      return { label: 'Opening the quote — pre-filled and ready to review.', path };
    }
    case 'prefill_campaign': {
      const draft = validateOttoCampaignDraft(action.draft);
      const nonce = stashOttoDraft(draft);
      const path = `/marketing/campaigns/create?draft=${encodeURIComponent(nonce)}`;
      navigate(path);
      return { label: 'Opening the campaign — pre-filled and ready to review.', path };
    }
    default: {
      const exhaustive: never = action.kind;
      throw new Error(`Unknown Otto action: ${String(exhaustive)}`);
    }
  }
}
